import { CloseOutlined } from '@mui/icons-material'
import { Autocomplete, TextField, Chip, IconButton } from '@mui/material'
import React, { useEffect, useState } from 'react'

const CreatableDropdown = ({
  options = [],
  getOptionLabel = (option) => (option.isNew ? option.name : option.name || ''),
  fullWidth = true,
  onChange,
  defaultValue,
  disabled = false,
  errorMessage = '',
  isOptionEqualToValue = (opt, val) => opt?.value === val?.value,
  placeHolder = '',
  name,
  value,
  allowNewOption = false,
  validateNewOption = (inputValue) => inputValue.length === 10, // Example: Allow only 10-digit input
}) => {
  const [inputValue, setInputValue] = useState('')
  const [customOptions, setCustomOptions] = useState(options)
  const [inputError, setInputError] = useState(false)

  useEffect(() => {
    setCustomOptions(options)
  }, [options])

  const handleKeyDown = (event) => {
    // Allow only numeric keys and control keys (Backspace, Delete, Arrow keys, etc.)
    if (
      !/^[0-9]$/.test(event.key) &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(
        event.key
      )
    ) {
      event.preventDefault() // Prevent the typing of special characters or letters
    }

    // Prevent input if it exceeds 10 digits
    if (
      inputValue.length >= 10 &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(
        event.key
      )
    ) {
      event.preventDefault()
    }
  }

  const handleInputChange = (event, newInputValue) => {
    // Ensure only numbers and max length of 10
    if (/^[0-9]{0,10}$/.test(newInputValue)) {
      setInputValue(newInputValue)
    }

    // Validate the input value
    const isValid =
      newInputValue.length === 10 && /^[0-9]+$/.test(newInputValue)
    setInputError(!isValid)
  }

  const handleChange = (event, newValue) => {
    if (allowNewOption && typeof newValue?.value === 'string') {
      const isValid =
        validateNewOption(newValue.value) && /^[0-9]+$/.test(newValue.value)
      const option = customOptions.find((elem) => elem.value === newValue.value)
      if (isValid) {
        if (!option) {
          const newOption = {
            name: newValue.value,
            value: newValue.value,
            isNew: true,
            created: true,
          }
          setCustomOptions((prev) => [...prev, newOption]) // Add to options
          onChange?.(event, newOption)
        } else {
          onChange?.(event, option)
        }
      }
      onChange?.(event, newValue)
    }
    if (allowNewOption && typeof newValue === 'string') {
      const isValid = validateNewOption(newValue) && /^[0-9]+$/.test(newValue)
      if (isValid) {
        const newOption = {
          name: newValue,
          value: newValue,
          isNew: true,
          created: true,
        }
        setCustomOptions((prev) => [...prev, newOption]) // Add to options
        onChange?.(event, newOption)
      }
    }
  }

  // Function to remove a newly added option
  const handleRemoveOption = (optionToRemove) => {
    setCustomOptions((prev) =>
      prev.filter((opt) => opt.value !== optionToRemove.value)
    )
  }

  return (
    <Autocomplete
      forcePopupIcon={true}
      options={[
        ...customOptions,
        ...(allowNewOption &&
        inputValue &&
        validateNewOption(inputValue) &&
        /^[0-9]+$/.test(inputValue) &&
        !customOptions.some((elem) => elem.value === inputValue)
          ? [
              {
                name: `Add "${inputValue}"`,
                value: inputValue,
                isNew: true,
                created: false,
              },
            ]
          : []),
      ]}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      defaultValue={defaultValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      size="small"
      fullWidth={fullWidth}
      freeSolo={allowNewOption}
      value={value}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          {option.name}
          {option.isNew && option.created && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation() // Prevent selection on click
                handleRemoveOption(option)
              }}
              size="small"
              style={{ marginLeft: 2, cursor: 'pointer' }}
            >
              <CloseOutlined fontSize="small" />
            </IconButton>
          )}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          value={inputValue}
          name={name}
          onKeyDown={handleKeyDown} // Add the onKeyDown event here
          placeholder={placeHolder}
          error={allowNewOption && inputError}
          helperText={allowNewOption && inputError ? errorMessage : ''}
        />
      )}
    />
  )
}

export default CreatableDropdown
